/**
 * MessageBubble — single chat message with markdown rendering
 */

import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLongPress } from '../../../hooks/useLongPress';
import type { AgentFeedbackRating, ChatMessage } from '../../../features/chat/api';
import { AgentAnswerMeta } from './AgentAnswerMeta';
import { preprocessMarkdown } from './types';
import styles from '../AiConsultantPage.module.css';

const remarkPluginsConfig = [remarkGfm];
const markdownComponents = {
  table: ({node: _node, ...props}: any) => <table className={styles.markdownTable} {...props} />,
  th: ({node: _node, ...props}: any) => <th className={styles.markdownTh} {...props} />,
  td: ({node: _node, ...props}: any) => <td className={styles.markdownTd} {...props} />,
  p: ({node: _node, ...props}: any) => <p className={styles.markdownParagraph} {...props} />,
  ul: ({node: _node, ...props}: any) => <ul className={styles.markdownList} {...props} />,
  ol: ({node: _node, ...props}: any) => <ol className={styles.markdownList} {...props} />,
  li: ({node: _node, ...props}: any) => <li className={styles.markdownListItem} {...props} />,
  a: ({node: _node, ...props}: any) => <a className={styles.markdownLink} target="_blank" rel="noreferrer" {...props} />,
  code: ({node: _node, className, ...props}: any) => (
    <code
      className={`${className || ''} ${styles.markdownCode}`}
      {...props}
    />
  ),
  pre: ({node: _node, ...props}: any) => <pre className={styles.markdownPre} {...props} />,
};

interface MessageBubbleProps {
  msg: ChatMessage;
  onLongPress: (msgId: string, event: React.TouchEvent | React.MouseEvent) => void;
  onPreviewImage: (url: string) => void;
  onAskFollowup?: (value: string) => void;
  onFeedback?: (messageId: string, value: AgentFeedbackRating) => void;
  feedbackPending?: boolean;
  bubbleRef: (el: HTMLDivElement | null) => void;
}

export const MessageBubble = forwardRef<HTMLDivElement, MessageBubbleProps>(function MessageBubbleFn(
  {
    msg,
    onLongPress,
    onPreviewImage,
    onAskFollowup,
    onFeedback,
    feedbackPending,
    bubbleRef,
  },
  forwardedRef,
) {
  const { handlers, isPressing } = useLongPress({
    threshold: 500,
    onLongPress: (e) => onLongPress(msg.id, e),
  });

  return (
    <motion.div
      ref={forwardedRef}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
      className={`${styles.messageRow} ${msg.role === 'user' ? styles.messageRowUser : styles.messageRowAi}`}
    >
      <div
        ref={bubbleRef}
        data-message-role={msg.role}
        className={`${styles.messageBubble} ${isPressing ? styles.bubblePressing : ''}`}
        {...handlers}
      >
        <div className={`${styles.bubbleContent} ${msg.role === 'user' ? styles.bubbleUser : styles.bubbleAi}`}>
          {msg.attachments && msg.attachments.length > 0 && (
            <div className={styles.messageAttachments}>
              {msg.attachments.map((att, idx) => (
                <div key={`${att.attachmentId || att.url}-${idx}`} className={styles.messageAttachmentItem}>
                  {att.mimeType.startsWith('image/') ? (
                    <img
                      src={att.url.startsWith('oss://') ? '#' : att.url}
                      alt={att.fileName}
                      className={styles.messageAttachmentImage}
                      onClick={() => onPreviewImage(att.url)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className={styles.messageAttachmentFile}>
                      <FileText className={styles.messageAttachmentIcon} />
                      <span>{att.fileName}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className={styles.messageMarkdown}>
            <ReactMarkdown
              remarkPlugins={remarkPluginsConfig}
              components={markdownComponents}
            >
              {preprocessMarkdown(msg.text)}
            </ReactMarkdown>
          </div>
          {msg.role !== 'user' && (
            <AgentAnswerMeta
              messageId={msg.id}
              meta={msg.agentMeta}
              feedbackPending={feedbackPending}
              onAskFollowup={onAskFollowup}
              onFeedback={onFeedback}
            />
          )}
        </div>
        <div className={`${styles.messageTime} ${msg.role === 'user' ? styles.timeRight : ''}`} data-export-hide>
          {msg.time}
        </div>
      </div>
    </motion.div>
  );
});
